import React, { MouseEvent, useState } from 'react';
import useTheme from '../../hooks/ui/useTheme';
import classnames from 'classnames';
import { Minus, Plus } from 'lucide-react';

interface Props {
  count: number;
  increase: (count: number) => void;
  decrease: (count: number) => void;
  allowZero?: boolean;
  iconColor?: string;
  classes?: {
    container?: string;
    quantity?: string;
    buttons?: string;
  };
}

export default function CounterButton(props: Props) {
  const { count, increase, decrease, allowZero, iconColor, classes } = props;

  const theme = useTheme();

  const [minusHighlighted, setMinusHighlighted] = useState(false);
  const [plusHighlighted, setPlusHighlighted] = useState(false);

  function onDecrease(event: MouseEvent<HTMLButtonElement>): void {
    event.stopPropagation();

    if (count <= 1 && !allowZero) {
      return;
    }

    decrease(count - 1);
  }

  function getIconColor(active: boolean) {
    if (active) {
      return theme.color.brand.primary;
    }

    return iconColor ?? theme.color.mobile.tertiary;
  }

  return (
    <div className={classnames(container, classes?.container)}>
      <button
        type="button"
        onClick={onDecrease}
        onMouseOver={() => setMinusHighlighted(true)}
        onMouseLeave={() => setMinusHighlighted(false)}
        className={buttonStyle}
      >
        <Minus className={classes?.buttons ?? iconStyle} color={getIconColor(minusHighlighted)} />
      </button>
      <h6 key={count} className={classnames(classes?.quantity, 'text-muted-foreground')}>
        {count}
      </h6>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          increase(count + 1);
        }}
        onMouseOver={() => setPlusHighlighted(true)}
        onMouseLeave={() => setPlusHighlighted(false)}
        className={buttonStyle}
      >
        <Plus className={classes?.buttons ?? iconStyle} color={getIconColor(plusHighlighted)} />
      </button>
    </div>
  );
}

const container =
  'w-full bg-brand-card border-[1px] flex justify-between items-center p-sm lg:px-md gap-x-2xs rounded-full';

const iconStyle = 'h-5 w-5';

const buttonStyle =
  "before:relative after:content-[''] after:absolute after:-top-4 after:-bottom-4 after:-left-5 after:-right-5";
